import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import Reaptcha from 'reaptcha';
import { toast } from 'react-toastify';
import withSizes from 'react-sizes';
import { makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import ButtonBase from '@material-ui/core/ButtonBase';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import InputBase from '@material-ui/core/InputBase';
import Typography from '@material-ui/core/Typography';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { sendMail } from 'containers/Main/actions';
import {
  makeSelectSendMailProcessing,
  makeSelectSendMailStatus,
} from 'containers/Main/selectors';
import Mixpanel from 'utils/mixpanelService';

const useStyles = makeStyles({
  formInput: {
    fontFamily: 'OpenSansRegular',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: '1.7em',
    borderRadius: 5,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D1D1D1',
    width: '100%',
    color: '#999999',
    padding: '14px 4px 14px 15px',
    backgroundColor: '#FFFFFF',
    '& > input, > textarea': {
      padding: 0,
      '&::placeholder': {
        color: '#999999',
        opacity: 1,
      },
      '&:focus': {
        color: '#3e3e3e',
      },
    },
  },
  formInputError: {
    borderColor: 'rgb(211, 84, 85)',
  },
  button: {
    color: '#FFFFFF',
    borderRadius: 5,
    letterSpacing: 'normal',
    textAlign: 'right',
    fontSize: 17,
    marginTop: 18,
    fontFamily: 'PoppinsSemiBold',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: '#FF9902',
    padding: '11px 20px',
    float: 'left',
    transition: 'all 300ms ease 0ms',
    position: 'relative',
    '& > svg': {
      position: 'absolute',
      opacity: 0,
      transition: 'all 300ms ease 0ms',
      height: 19,
      right: 8,
    },
    '&:hover': {
      paddingRight: 32,
      letterSpacing: 4,
      '& > svg': {
        opacity: 1,
        marginLeft: 0,
      },
    },
  },
  checkboxTitle: {
    fontFamily: 'OpenSansSemiBold',
    fontWeight: '600',
    fontSize: 18,
    lineHeight: '1.7em',
    color: '#666666',
    marginBottom: 9,
  },
  checkboxLabel: {
    fontFamily: 'OpenSansRegular',
    fontWeight: '400',
    fontSize: 18,
    lineHeight: '1.7em',
    color: '#666666',
  },
  checkboxWrapper: {
    width: '100%',
  },
  checkboxRoot: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  checkboxIcon: {
    borderRadius: 4,
    width: 18,
    height: 18,
    border: '1px solid #D1D1D1',
    backgroundColor: '#FFFFFF',
    '$root.Mui-focusVisible &': {
      outline: '2px auto #FFFFFF',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: '#FFFFFF',
    },
  },
  checkboxIconError: {
    borderColor: 'rgb(211, 84, 85)',
  },
  checkboxCheckedIcon: {
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%2301619b'/%3E%3C/svg%3E\")",
      content: '""',
    },
  },
});

function ContactForm({ form, isCompact, submitLabel }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const processing = useSelector(makeSelectSendMailProcessing());
  const emailSent = useSelector(makeSelectSendMailStatus());
  const reaptcha = useRef();
  const [data, setData] = useState({});
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState([]);

  useEffect(() => {
    if (emailSent) {
      toast(
        'Thank you for your message. Our team will get in contact with you.',
        { type: 'success' },
      );

      window.gtag('event', 'conversion', {
        send_to: 'AW-726549912/BNmWCJXitvEBEJiLudoC',
      });

      Mixpanel.track('Solutions Home Page - Contact Form submitted');

      setData({});
      setVerified(false);
      reaptcha.current.reset();
    }
  }, [emailSent]);

  const onChange = e => {
    const formError = error.filter(el => el !== e.target.name);
    setError(formError);

    setData(
      update(data, {
        [e.target.name]: { $set: e.target.value },
      }),
    );
  };

  const onCheckboxChange = name => e => {
    const formError = error.filter(el => el !== name);
    setError(formError);

    let checkboxValues = _get(data, name, []);
    if (checkboxValues.includes(e.target.value)) {
      checkboxValues = data[name].filter(el => el !== e.target.value);
    } else {
      checkboxValues = update(checkboxValues, { $push: [e.target.value] });
    }

    setData(
      update(data, {
        [name]: { $set: checkboxValues },
      }),
    );
  };

  const onSubmit = () => {
    const formError = [];

    form.forEach(item => {
      if (item.type !== 'submit') {
        if (_isEmpty(data[item.name])) formError.push(item.name);
      }
    });

    if (!verified) formError.push('reaptcha');

    if (formError.length > 0) {
      setError(formError);
    } else {
      dispatch(
        sendMail({
          type: 'contact',
          ...data,
        }),
      );
    }
  };

  return (
    <Grid container spacing={2}>
      {form.map(item => {
        if (item.type === 'input') {
          return (
            <Grid item xs={item.xs} sm={item.sm} md={item.md}>
              <InputBase
                classes={{
                  root: _classNames({
                    [classes.formInput]: true,
                    [classes.formInputError]: error.includes(item.name),
                  }),
                }}
                placeholder={item.placeholder}
                name={item.name}
                value={data[item.name]}
                onChange={onChange}
              />
            </Grid>
          );
        }
        if (item.type === 'textarea') {
          return (
            <Grid item xs={12}>
              <InputBase
                placeholder={item.placeholder}
                name={item.name}
                multiline
                rows={6}
                classes={{
                  root: _classNames({
                    [classes.formInput]: true,
                    [classes.formInputError]: error.includes(item.name),
                  }),
                }}
                value={data[item.name]}
                onChange={onChange}
              />
            </Grid>
          );
        }
        if (item.type === 'checkbox') {
          return (
            <Grid item xs={12}>
              <Typography className={classes.checkboxTitle}>
                {item.header}
              </Typography>
              {item.options.map(option => (
                <FormControlLabel
                  value={option.value}
                  onChange={onCheckboxChange(item.name)}
                  className={classes.checkboxWrapper}
                  classes={{
                    label: classes.checkboxLabel,
                  }}
                  control={
                    <Checkbox
                      className={classes.checkboxRoot}
                      disableRipple
                      color="default"
                      checked={_get(data, item.name, []).includes(option.value)}
                      checkedIcon={
                        <span
                          className={_classNames(
                            classes.checkboxIcon,
                            classes.checkboxCheckedIcon,
                          )}
                        />
                      }
                      icon={
                        <span
                          className={_classNames(classes.checkboxIcon, {
                            [classes.checkboxIconError]: error.includes(
                              item.name,
                            ),
                          })}
                        />
                      }
                    />
                  }
                  label={option.label}
                  labelPlacement="right"
                />
              ))}
            </Grid>
          );
        }
        return null;
      })}
      <Grid item xs={12}>
        <Reaptcha
          ref={reaptcha}
          size={isCompact ? 'compact' : 'normal'}
          sitekey="6Lc5250UAAAAAJ91NxVgTpFG-bv1Z4Ch3x7sGzgR"
          onVerify={() => setVerified(true)}
        />
      </Grid>
      <Grid item xs={12} className={classes.buttonWrapper}>
        <ButtonBase
          disableRipple
          classes={{ root: classes.button }}
          onClick={onSubmit}
        >
          {processing ? 'Sending...' : submitLabel}
          <ChevronRightIcon />
        </ButtonBase>
      </Grid>
    </Grid>
  );
}

const mapSizesToProps = ({ width }) => ({
  isCompact: width < 420,
});

ContactForm.propTypes = {
  form: PropTypes.array,
  isCompact: PropTypes.bool,
  submitLabel: PropTypes.string,
};

export default withSizes(mapSizesToProps)(ContactForm);
